import { useEffect, useState } from "react";
import { Link, useParams, Outlet, useLocation } from "react-router-dom";
import { Dialog, Transition, Switch, Menu } from "@headlessui/react";
import AnimatedPage from "../../components/animatedPage/AnimatedPage";

import * as api from "./apiService";
import { toast } from "react-toastify";

const formatPhoneNumber = (phoneNumberString) => {
  var cleaned = ("" + phoneNumberString).replace(/\D/g, "");
  var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    var intlCode = match[1] ? "+1 " : "";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  }

  return null;
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const CustomerDetails = () => {
  const { customerId } = useParams();
  const [customerDetails, setCustomerDetails] = useState(null);
  const [tMinusFirstJobNudgeHours, setTMinusFirstJobNudgeHours] = useState(4);
  const [tMinusFinalJobNudgeHours, setTMinusFinalJobNudgeHours] = useState(1);
  const [firstJobNudgeComment, setFirstJobNudgeComment] = useState("");
  const [finalJobNudgeComment, setFinalJobNudgeComment] = useState("");

  useEffect(() => {
    getCustomerDetails();
  }, [customerId]);

  const getCustomerDetails = async () => {
    try {
      const { data } = await api.getCustomerDetails(customerId);

      setCustomerDetails(data);
      setTMinusFirstJobNudgeHours(data.settings.t_minus_first_job_nudge_hours);
      setTMinusFinalJobNudgeHours(data.settings.t_minus_final_job_nudge_hours);
      setFirstJobNudgeComment(data.settings.first_job_nudge_comment);
      setFinalJobNudgeComment(data.settings.final_job_nudge_comment);
    } catch (err) {}
  };

  const updateShowSpendingInfo = async () => {
    const request = {
      show_spending_info: !customerDetails.settings.show_spending_info,
    };

    try {
      const { data } = await api.updateCustomerSetting(
        customerDetails.settings.id,
        request,
      );

      const updatedCustomerDetails = {
        ...customerDetails,
        settings: {
          ...customerDetails.settings,
          show_spending_info: data.show_spending_info,
        },
      };

      setCustomerDetails(updatedCustomerDetails);
    } catch (error) {}
  };

  const updateAutoAssignment = async () => {
    const request = {
      enable_auto_assignment: !customerDetails.settings.enable_auto_assignment,
    };

    try {
      const { data } = await api.updateCustomerSetting(
        customerDetails.settings.id,
        request,
      );

      const updatedCustomerDetails = {
        ...customerDetails,
        settings: {
          ...customerDetails.settings,
          enable_auto_assignment: data.enable_auto_assignment,
        },
      };

      setCustomerDetails(updatedCustomerDetails);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const updateApprovalProcess = async () => {
    const request = {
      enable_approval_process:
        !customerDetails.settings.enable_approval_process,
    };

    try {
      const { data } = await api.updateCustomerSetting(
        customerDetails.settings.id,
        request,
      );

      const updatedCustomerDetails = {
        ...customerDetails,
        settings: {
          ...customerDetails.settings,
          enable_approval_process: data.enable_approval_process,
        },
      };

      setCustomerDetails(updatedCustomerDetails);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const updateAllowCancelJob = async () => {
    const request = {
      allow_cancel_job: !customerDetails.settings.allow_cancel_job,
    };

    try {
      const { data } = await api.updateCustomerSetting(
        customerDetails.settings.id,
        request,
      );

      const updatedCustomerDetails = {
        ...customerDetails,
        settings: {
          ...customerDetails.settings,
          allow_cancel_job: data.allow_cancel_job,
        },
      };

      setCustomerDetails(updatedCustomerDetails);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const updateEnableFlightBasedScheduledCleaning = async () => {
    const request = {
      enable_flight_based_scheduled_cleaning:
        !customerDetails.settings.enable_flight_based_scheduled_cleaning,
    };

    try {
      const { data } = await api.updateCustomerSetting(
        customerDetails.settings.id,
        request,
      );

      const updatedCustomerDetails = {
        ...customerDetails,
        settings: {
          ...customerDetails.settings,
          enable_flight_based_scheduled_cleaning:
            data.enable_flight_based_scheduled_cleaning,
        },
      };

      setCustomerDetails(updatedCustomerDetails);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const updateHideAddonsServices = async () => {
    const request = {
      hide_addons_services: !customerDetails.settings.hide_addons_services,
    };

    try {
      const { data } = await api.updateCustomerSetting(
        customerDetails.settings.id,
        request,
      );

      const updatedCustomerDetails = {
        ...customerDetails,
        settings: {
          ...customerDetails.settings,
          hide_addons_services: data.hide_addons_services,
        },
      };

      setCustomerDetails(updatedCustomerDetails);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const updateEnableJobNudges = async () => {
    const request = {
      enable_job_nudges: !customerDetails.settings.enable_job_nudges,
    };

    try {
      const { data } = await api.updateCustomerSetting(
        customerDetails.settings.id,
        request,
      );
      const updatedCustomerDetails = {
        ...customerDetails,
        settings: {
          ...customerDetails.settings,
          enable_job_nudges: data.enable_job_nudges,
        },
      };
      setCustomerDetails(updatedCustomerDetails);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const updateJobNudgeValues = async () => {
    const request = {
      t_minus_first_job_nudge_hours: parseInt(tMinusFirstJobNudgeHours),
      t_minus_final_job_nudge_hours: parseInt(tMinusFinalJobNudgeHours),
      first_job_nudge_comment: firstJobNudgeComment,
      final_job_nudge_comment: finalJobNudgeComment,
    };

    try {
      const { data } = await api.updateCustomerSetting(
        customerDetails.settings.id,
        request,
      );
      const updatedCustomerDetails = {
        ...customerDetails,
        settings: {
          ...customerDetails.settings,
          t_minus_first_job_nudge_hours: data.t_minus_first_job_nudge_hours,
          t_minus_final_job_nudge_hours: data.t_minus_final_job_nudge_hours,
          first_job_nudge_comment: data.first_job_nudge_comment,
          final_job_nudge_comment: data.final_job_nudge_comment,
        },
      };
      setCustomerDetails(updatedCustomerDetails);
      toast.success("Job nudge settings updated successfully.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const updateRequestPriority = async () => {
    const request = {
      enable_request_priority:
        !customerDetails.settings.enable_request_priority,
    };

    try {
      const { data } = await api.updateCustomerSetting(
        customerDetails.settings.id,
        request,
      );

      const updatedCustomerDetails = {
        ...customerDetails,
        settings: {
          ...customerDetails.settings,
          enable_request_priority: data.enable_request_priority,
        },
      };

      setCustomerDetails(updatedCustomerDetails);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const updateShowJobPrice = async () => {
    const request = {
      show_job_price: !customerDetails.settings.show_job_price,
    };

    try {
      const { data } = await api.updateCustomerSetting(
        customerDetails.settings.id,
        request,
      );

      const updatedCustomerDetails = {
        ...customerDetails,
        settings: {
          ...customerDetails.settings,
          show_job_price: data.show_job_price,
        },
      };

      setCustomerDetails(updatedCustomerDetails);
    } catch (error) {}
  };

  return (
    <AnimatedPage>
      <div className="mx-auto mt-6 max-w-7xl px-4 sm:px-6 lg:px-8">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
          <div className="sm:col-span-1">
            <dt className="text-md font-medium text-gray-500">Phone</dt>
            <dd className="mt-1 text-md text-gray-900">
              {customerDetails?.phone_number
                ? formatPhoneNumber(customerDetails.phone_number)
                : "Not specified"}
            </dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-md font-medium text-gray-500">Email</dt>
            <dd className="mt-1 text-md text-gray-900">
              {customerDetails?.emailAddress
                ? customerDetails?.emailAddress
                : "Not specified"}
            </dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-md font-medium text-gray-500">
              Billing Address
            </dt>
            <dd className="mt-1 text-md text-gray-900">
              {customerDetails?.billingAddress
                ? customerDetails?.billingAddress
                : "Not specified"}
            </dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-md font-medium text-gray-500">Price List</dt>
            <dd className="mt-1 text-md text-gray-900">
              {customerDetails?.settings?.price_list.name}
            </dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-md font-medium text-gray-500">
              Retainer Amount (monthly)
            </dt>
            <dd className="mt-1 text-md text-gray-900">
              {customerDetails?.settings?.retainer_amount
                ? "$" +
                  customerDetails?.settings?.retainer_amount.toLocaleString()
                : "Not specified"}
            </dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-md font-medium text-gray-500">Active</dt>
            <dd className="mt-1 text-md text-gray-900">
              {customerDetails?.active ? "Yes" : "No"}
            </dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-md font-medium text-gray-500">About</dt>
            <dd className="mt-1 max-w-prose space-y-5 text-md text-gray-900">
              {customerDetails?.about
                ? customerDetails?.about
                : "Not specified"}
            </dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-md font-medium text-gray-500">
              Exterior Service Checker
            </dt>
            <dd className="mt-1 max-w-prose space-y-5 text-md text-gray-900">
              {customerDetails?.exterior_service_checker
                ? customerDetails?.exterior_service_checker
                : "Not specified"}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-md font-medium text-gray-500">Instructions</dt>
            <dd className="mt-1 text-md text-gray-900">
              {customerDetails?.settings?.special_instructions && (
                <div className="h-[120px] overflow-y-auto p-2 border-none pb-8">
                  <p
                    dangerouslySetInnerHTML={{
                      __html:
                        customerDetails?.settings?.special_instructions?.replace(
                          /\r\n|\n/g,
                          "<br />",
                        ),
                    }}
                  />
                </div>
              )}

              {!customerDetails?.settings?.special_instructions && (
                <div>Not specified</div>
              )}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-md font-medium text-gray-500">Billing Info</dt>
            <dd className="mt-1 text-md text-gray-900">
              {customerDetails?.billingInfo && (
                <div className="h-[260px] overflow-y-auto p-2 border-none pb-8">
                  <p
                    dangerouslySetInnerHTML={{
                      __html: customerDetails?.billingInfo?.replace(
                        /\r\n|\n/g,
                        "<br />",
                      ),
                    }}
                  />
                </div>
              )}

              {!customerDetails?.billingInfo && <div>Not specified</div>}
            </dd>
          </div>
        </dl>
      </div>

      {/* Settings list */}
      <div className="mx-auto mt-8 max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
        <div className="divide-y divide-gray-200 pt-6">
          <div className="">
            <div>
              <h2 className="text-lg font-medium leading-6 text-gray-900">
                Settings
              </h2>
              <p className="mt-1 text-md text-gray-500">
                Control what all users associated with this customer can see and
                do.
              </p>
            </div>
            <ul role="list" className="mt-2 divide-y divide-gray-200">
              <Switch.Group
                as="li"
                className="flex items-center justify-between py-4"
              >
                <div className="flex flex-col">
                  <Switch.Label
                    as="p"
                    className="text-md font-medium text-gray-900"
                    passive
                  >
                    Show Spending Info
                  </Switch.Label>
                  <Switch.Description className="text-md text-gray-500">
                    Controls whether to show the "Spending Info" in the customer
                    dashboard for this customer.
                  </Switch.Description>
                </div>
                <Switch
                  checked={customerDetails?.settings?.show_spending_info}
                  onChange={() => updateShowSpendingInfo()}
                  className={classNames(
                    customerDetails?.settings?.show_spending_info
                      ? "bg-red-500"
                      : "bg-gray-200",
                    "relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={classNames(
                      customerDetails?.settings?.show_spending_info
                        ? "translate-x-5"
                        : "translate-x-0",
                      "inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                    )}
                  />
                </Switch>
              </Switch.Group>
              <Switch.Group
                as="li"
                className="flex items-center justify-between py-4"
              >
                <div className="flex flex-col">
                  <Switch.Label
                    as="p"
                    className="text-md font-medium text-gray-900"
                    passive
                  >
                    Show Job Price
                  </Switch.Label>
                  <Switch.Description className="text-md text-gray-500">
                    Controls whether customer users can view pricing information
                    on jobs. Customer users must have both customer-level and
                    individual user-level pricing permissions enabled to access
                    pricing details.
                    <br></br>
                    When disabled, pricing information is hidden from customer
                    users. Project managers and vendor users cannot view
                    customer pricing information.
                  </Switch.Description>
                </div>
                <Switch
                  checked={customerDetails?.settings?.show_job_price}
                  onChange={() => updateShowJobPrice()}
                  className={classNames(
                    customerDetails?.settings?.show_job_price
                      ? "bg-red-500"
                      : "bg-gray-200",
                    "relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={classNames(
                      customerDetails?.settings?.show_job_price
                        ? "translate-x-5"
                        : "translate-x-0",
                      "inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                    )}
                  />
                </Switch>
              </Switch.Group>
              <Switch.Group
                as="li"
                className="flex items-center justify-between py-4"
              >
                <div className="flex flex-col">
                  <Switch.Label
                    as="p"
                    className="text-md font-medium text-gray-900"
                    passive
                  >
                    Allow Cancel Job
                  </Switch.Label>
                  <Switch.Description className="text-md text-gray-500">
                    Controls whether this customer can cancel a job while the
                    status is "submitted" or "accepted"
                  </Switch.Description>
                </div>
                <Switch
                  checked={customerDetails?.settings?.allow_cancel_job}
                  onChange={() => updateAllowCancelJob()}
                  className={classNames(
                    customerDetails?.settings?.allow_cancel_job
                      ? "bg-red-500"
                      : "bg-gray-200",
                    "relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={classNames(
                      customerDetails?.settings?.allow_cancel_job
                        ? "translate-x-5"
                        : "translate-x-0",
                      "inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                    )}
                  />
                </Switch>
              </Switch.Group>
              <Switch.Group
                as="li"
                className="flex items-center justify-between py-4"
              >
                <div className="flex flex-col">
                  <Switch.Label
                    as="p"
                    className="text-md font-medium text-gray-900"
                    passive
                  >
                    Enable Request Priority
                  </Switch.Label>
                  <Switch.Description className="text-md text-gray-500">
                    When enabled, users will have the option to mark a job
                    request as high priority during job creation. This will add
                    a red "High Priority" tag to the job.
                  </Switch.Description>
                </div>
                <Switch
                  checked={customerDetails?.settings?.enable_request_priority}
                  onChange={() => updateRequestPriority()}
                  className={classNames(
                    customerDetails?.settings?.enable_request_priority
                      ? "bg-red-500"
                      : "bg-gray-200",
                    "relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={classNames(
                      customerDetails?.settings?.enable_request_priority
                        ? "translate-x-5"
                        : "translate-x-0",
                      "inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                    )}
                  />
                </Switch>
              </Switch.Group>
              <Switch.Group
                as="li"
                className="flex items-center justify-between py-4"
              >
                <div className="flex flex-col">
                  <Switch.Label
                    as="p"
                    className="text-md font-medium text-gray-900"
                    passive
                  >
                    Enable Approval Process
                  </Switch.Label>
                  <Switch.Description className="text-md text-gray-500">
                    Enables a customer approval workflow for newly submitted
                    jobs.
                    <br></br>
                    When a job is created by a user who cannot confirm jobs,
                    LiveTakeoff notifies eligible customer approvers and the
                    internal operations team so the job can be reviewed before
                    moving forward.
                  </Switch.Description>
                </div>
                <Switch
                  checked={customerDetails?.settings?.enable_approval_process}
                  onChange={() => updateApprovalProcess()}
                  className={classNames(
                    customerDetails?.settings?.enable_approval_process
                      ? "bg-red-500"
                      : "bg-gray-200",
                    "relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={classNames(
                      customerDetails?.settings?.enable_approval_process
                        ? "translate-x-5"
                        : "translate-x-0",
                      "inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                    )}
                  />
                </Switch>
              </Switch.Group>
              <Switch.Group
                as="li"
                className="flex items-center justify-between py-4"
              >
                <div className="flex flex-col">
                  <Switch.Label
                    as="p"
                    className="text-md font-medium text-gray-900"
                    passive
                  >
                    Enable Auto Assignment
                  </Switch.Label>
                  <Switch.Description className="text-md text-gray-500">
                    Automatically assigns newly created jobs to the preferred
                    project manager configured for the selected airport.
                    <br></br>
                    The system will update the job status, assign all services,
                    and send assignment notifications automatically.
                    <br></br>
                    This only happens when the approval process is disabled for
                    the customer and the airport has a preferred project manager
                    configured
                  </Switch.Description>
                </div>
                <Switch
                  checked={customerDetails?.settings?.enable_auto_assignment}
                  onChange={() => updateAutoAssignment()}
                  className={classNames(
                    customerDetails?.settings?.enable_auto_assignment
                      ? "bg-red-500"
                      : "bg-gray-200",
                    "relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={classNames(
                      customerDetails?.settings?.enable_auto_assignment
                        ? "translate-x-5"
                        : "translate-x-0",
                      "inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                    )}
                  />
                </Switch>
              </Switch.Group>
              <Switch.Group
                as="li"
                className="flex items-center justify-between py-4"
              >
                <div className="flex flex-col">
                  <Switch.Label
                    as="p"
                    className="text-md font-medium text-gray-900"
                    passive
                  >
                    Enable Flight Based Scheduled Cleaning
                  </Switch.Label>
                  <Switch.Description className="text-md text-gray-500">
                    Enables flight-based cleaning recommendations for this
                    customer. LiveTakeoff tracks each active aircraft tail,
                    counts arrived flights since the last completed interior and
                    exterior cleaning services, and flags aircraft that may be
                    due for Level 1 or Level 2 cleaning.
                    <br></br>
                    When service is recommended, the system updates the aircraft
                    status and sends a cleaning report to help the team schedule
                    work proactively.
                  </Switch.Description>
                </div>
                <Switch
                  checked={
                    customerDetails?.settings
                      ?.enable_flight_based_scheduled_cleaning
                  }
                  onChange={() => updateEnableFlightBasedScheduledCleaning()}
                  className={classNames(
                    customerDetails?.settings
                      ?.enable_flight_based_scheduled_cleaning
                      ? "bg-red-500"
                      : "bg-gray-200",
                    "relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={classNames(
                      customerDetails?.settings
                        ?.enable_flight_based_scheduled_cleaning
                        ? "translate-x-5"
                        : "translate-x-0",
                      "inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                    )}
                  />
                </Switch>
              </Switch.Group>
              <Switch.Group
                as="li"
                className="flex items-center justify-between py-4"
              >
                <div className="flex flex-col">
                  <Switch.Label
                    as="p"
                    className="text-md font-medium text-gray-900"
                    passive
                  >
                    Hide Addons Services
                  </Switch.Label>
                  <Switch.Description className="text-md text-gray-500">
                    If enabled, addons services will be hidden when creating a
                    job for this customer. This only applies to users flagged as
                    Submitter Only.
                  </Switch.Description>
                </div>
                <Switch
                  checked={customerDetails?.settings?.hide_addons_services}
                  onChange={() => updateHideAddonsServices()}
                  className={classNames(
                    customerDetails?.settings?.hide_addons_services
                      ? "bg-red-500"
                      : "bg-gray-200",
                    "relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={classNames(
                      customerDetails?.settings?.hide_addons_services
                        ? "translate-x-5"
                        : "translate-x-0",
                      "inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                    )}
                  />
                </Switch>
              </Switch.Group>
              <Switch.Group
                as="li"
                className="flex items-center justify-between py-4"
              >
                <div className="flex flex-col">
                  <Switch.Label
                    as="p"
                    className="text-md font-medium text-gray-900"
                    passive
                  >
                    Enable Job Nudges
                  </Switch.Label>
                  <Switch.Description className="text-md text-gray-500">
                    If enabled, jobs that have been in the assigned/WIP status
                    reaching the complete by time with the configured time will
                    send nudge notifications to the assigned project managers as
                    job comments/emails.
                  </Switch.Description>
                </div>
                <Switch
                  checked={customerDetails?.settings?.enable_job_nudges}
                  onChange={() => updateEnableJobNudges()}
                  className={classNames(
                    customerDetails?.settings?.enable_job_nudges
                      ? "bg-red-500"
                      : "bg-gray-200",
                    "relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={classNames(
                      customerDetails?.settings?.enable_job_nudges
                        ? "translate-x-5"
                        : "translate-x-0",
                      "inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                    )}
                  />
                </Switch>
              </Switch.Group>
              {customerDetails?.settings?.enable_job_nudges && (
                <div className="px-12 py-4 ">
                  <div className="space-y-6 sm:space-y-5 ">
                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:border-t sm:border-gray-200 sm:pt-5">
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                      >
                        T Minus First Job Nudge in Hours
                      </label>
                      <div className="mt-1 sm:col-span-2 sm:mt-0">
                        <input
                          type="text"
                          name="tMinusFirstJobNudgeHours"
                          id="tMinusFirstJobNudgeHours"
                          value={tMinusFirstJobNudgeHours}
                          onChange={(e) =>
                            setTMinusFirstJobNudgeHours(e.target.value)
                          }
                          className="block w-full max-w-lg rounded-md border-gray-300 shadow-sm
                                    focus:border-sky-500 focus:ring-sky-500 sm:max-w-xs sm:text-sm"
                        />
                      </div>
                    </div>
                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:border-t sm:border-gray-200 sm:pt-5">
                      <label
                        htmlFor="about"
                        className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                      >
                        First Job Nudge Comment
                      </label>
                      <div className="mt-1 sm:col-span-2 sm:mt-0">
                        <textarea
                          id="firstJobNudgeComment"
                          name="firstJobNudgeComment"
                          value={firstJobNudgeComment}
                          onChange={(e) =>
                            setFirstJobNudgeComment(e.target.value)
                          }
                          rows={2}
                          className="block w-full max-w-lg rounded-md border-gray-300 shadow-sm
                                        focus:border-sky-500 focus:ring-sky-500 sm:text-sm h-24"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-6 sm:space-y-5">
                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:border-t sm:border-gray-200 sm:pt-5">
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                      >
                        T Minus Final Job Nudge in Hours
                      </label>
                      <div className="mt-1 sm:col-span-2 sm:mt-0">
                        <input
                          type="text"
                          name="tMinusFinalJobNudgeHours"
                          id="tMinusFinalJobNudgeHours"
                          value={tMinusFinalJobNudgeHours}
                          onChange={(e) =>
                            setTMinusFinalJobNudgeHours(e.target.value)
                          }
                          className="block w-full max-w-lg rounded-md border-gray-300 shadow-sm
                                    focus:border-sky-500 focus:ring-sky-500 sm:max-w-xs sm:text-sm"
                        />
                      </div>
                    </div>
                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:border-t sm:border-gray-200 sm:pt-5">
                      <label
                        htmlFor="about"
                        className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                      >
                        Final Job Nudge Comment
                      </label>
                      <div className="mt-1 sm:col-span-2 sm:mt-0">
                        <textarea
                          id="finalJobNudgeComment"
                          name="finalJobNudgeComment"
                          value={finalJobNudgeComment}
                          onChange={(e) =>
                            setFinalJobNudgeComment(e.target.value)
                          }
                          rows={2}
                          className="block w-full max-w-lg rounded-md border-gray-300 shadow-sm
                                        focus:border-sky-500 focus:ring-sky-500 sm:text-sm h-24"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex justify-end">
                    <button
                      type="button"
                      onClick={() => updateJobNudgeValues()}
                      className="ml-3 inline-flex justify-center rounded-md border
                              border-transparent bg-red-600 py-2 px-4 text-sm font-medium
                                text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2
                                focus:ring-red-500 focus:ring-offset-2"
                    >
                      Update
                    </button>
                  </div>
                </div>
              )}
            </ul>
          </div>
        </div>
      </div>
    </AnimatedPage>
  );
};

export default CustomerDetails;
